<template>
    <div class="record-link-more-modal-root">
        <Molded class="record-link-more-modal-context">
            <span class="record-link-more-modal-label">Lançamento Cadastrado</span>
            <div class="record-link-more-modal-description">
                {{ recordItem.recipientName || recordItem.description }}
            </div>
            <div class="record-link-more-modal-context-row">
                <div class="record-link-more-modal-subtext">{{ recordItem.dueDate }}</div>
                <div class="record-link-more-modal-value">{{ recordItem.value | currency }}</div>
            </div>
            <div class="record-link-more-modal-existing-balance" v-if="hasExistingBalance">
                Já possui baixa parcial de {{ existingPaidAmount | currency }} · Saldo atual:
                {{ recordItem.baseRemainingValue | currency }}
            </div>
            <div class="record-link-more-modal-balance">
                Pareado:
                <span
                    :class="recordItem.value < 0 ? 'record-link-more-modal-value-negative' : 'record-link-more-modal-value-positive'">
                    {{ (recordItem.value < 0 ? -recordItem.pairedTotal : recordItem.pairedTotal) | currency }} </span>
                        de {{ recordItem.baseRemainingValue | currency }}
                        <span v-if="recordItem.remainingValue > valueTolerance">
                            (saldo
                            <strong
                                :class="recordItem.value < 0 ? 'record-link-more-modal-value-negative' : 'record-link-more-modal-value-positive'">
                                {{ (recordItem.value < 0 ? -recordItem.remainingValue : recordItem.remainingValue) |
                                    currency }} </strong>)
                        </span>
            </div>

            <div class="record-link-more-modal-linked" v-if="linkedStatementItems.length > 0">
                <span class="record-link-more-modal-linked-label">Itens já vinculados</span>
                <div class="record-link-more-modal-linked-list">
                    <div class="record-link-more-modal-linked-item" v-for="item in linkedStatementItems" :key="item.id">
                        <span>{{ item.description }}</span>
                        <span>{{ item.value | currency }}</span>
                    </div>
                </div>
            </div>
        </Molded>

        <InputText title="Buscar item pendente por descrição, destinatário ou valor" field="search"
            formName="recordLinkMoreModalForm" v-model="search" />

        <div class="record-link-more-modal-selection-summary" v-if="selectedIds.length > 0">
            {{ selectedIds.length }} {{ selectedIds.length == 1 ? 'selecionado' : 'selecionados' }} · Total:
            {{ selectedTotal | currency }}
        </div>

        <div class="record-link-more-modal-list">
            <div v-if="filteredPendingItems.length == 0" class="record-link-more-modal-empty">
                Nenhum item pendente encontrado.
            </div>
            <Molded v-for="item in filteredPendingItems" :key="item.id" class="record-link-more-modal-item"
                :class="{ 'record-link-more-modal-item-selected': isSelected(item), 'record-link-more-modal-item-disabled': !canSelect(item) }"
                @click.native="toggleSelect(item)">
                <div class="record-link-more-modal-item-content">
                    <div class="record-link-more-modal-item-indicator"
                        :class="{ 'record-link-more-modal-item-indicator-selected': isSelected(item) }">
                        <i v-if="isSelected(item)" class="fa-solid fa-check"></i>
                    </div>
                    <div class="record-link-more-modal-item-info">
                        <div class="record-link-more-modal-item-description">{{ item.description }}</div>
                        <div class="record-link-more-modal-subtext">{{ item.dueDate }}</div>
                        <div class="record-link-more-modal-item-warning" v-if="!canSelect(item)">Saldo insuficiente
                        </div>
                    </div>
                    <div class="record-link-more-modal-item-side">
                        <div class="record-link-more-modal-item-value"
                            :class="{ 'record-link-more-modal-value-negative': item.value < 0, 'record-link-more-modal-value-positive': item.value >= 0 }">
                            {{ item.value | currency }}
                        </div>
                    </div>
                </div>
            </Molded>
        </div>

        <div class="record-link-more-modal-actions text-center">
            <Button _key="btnConfirmLinkMore" title="Vincular" size="medium" type="primary"
                :disabled="selectedIds.length == 0" :clicked="onConfirm" />
            <Button _key="btnCancelLinkMore" title="Cancelar" size="medium" type="danger" :clicked="onCancel" />
        </div>
    </div>
</template>
<script>

import Molded from "@/ui/layout/Molded";
import Button from "@/ui/forms/Button";
import InputText from "@/ui/forms/InputText";

import reconciliationRecordMixin, { withinBalance } from "../../mixins/reconciliationRecord.mixin.js";

export default {
    name: "RecordLinkMoreModal",
    mixins: [reconciliationRecordMixin],
    components: { Molded, Button, InputText },
    props: {
        recordItem: Object,
        statement: { type: Array, default: () => [] },
    },
    data() {
        return {
            search: "",
            selectedIds: [],
        };
    },
    computed: {
        pendingStatementItems() {
            return this.statement.filter((item) => item.status == 'pendente' && item.type == this.recordItem.type);
        },
        filteredPendingItems() {
            let term = (this.search || "").trim().toLowerCase();
            if (!term) return this.pendingStatementItems;

            return this.pendingStatementItems.filter((item) => {
                let description = (item.description || "").toLowerCase();
                let value = this.$options.filters.currency(item.value).toLowerCase();
                return description.includes(term) || value.includes(term);
            });
        },
        selectedItems() {
            return this.pendingStatementItems.filter((item) => this.selectedIds.includes(item.id));
        },
        selectedTotal() {
            return this.selectedItems.reduce((accumulator, item) => accumulator + Math.abs(item.value), 0);
        },
    },
    methods: {
        isSelected(item) {
            return this.selectedIds.includes(item.id);
        },
        canSelect(item) {
            if (this.isSelected(item)) return true;
            return withinBalance(this.selectedTotal + Math.abs(item.value), this.recordItem.remainingValue);
        },
        toggleSelect(item) {
            if (!this.canSelect(item)) return;

            let index = this.selectedIds.indexOf(item.id);
            if (index == -1) this.selectedIds.push(item.id);
            else this.selectedIds.splice(index, 1);
        },
        onConfirm() {
            this.$emit('select', this.selectedItems);
        },
        onCancel() {
            this.$emit('cancel');
        },
    },
};
</script>
<style scoped>
.record-link-more-modal-context {
    margin-bottom: 10px;
}

.record-link-more-modal-label {
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: var(--text-secondary);
}

.record-link-more-modal-description {
    font-size: 15px;
    font-weight: 600;
    color: var(--text-primary);
    margin-top: 4px;
}

.record-link-more-modal-context-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-top: 8px;
}

.record-link-more-modal-subtext {
    font-size: 12px;
    color: var(--text-secondary);
}

.record-link-more-modal-value {
    font-size: 18px;
    font-weight: 700;
    color: var(--text-primary);
}

.record-link-more-modal-existing-balance {
    font-size: 11.5px;
    color: var(--color-warning);
    font-weight: 600;
    margin-top: 6px;
}

.record-link-more-modal-balance {
    font-size: 12.5px;
    color: var(--color-brand);
    font-weight: 600;
    margin-top: 8px;
    padding-top: 8px;
    border-top: 1px solid var(--border-subtle);
}

.record-link-more-modal-linked {
    margin-top: 8px;
    padding-top: 8px;
    border-top: 1px solid var(--border-subtle);
}

.record-link-more-modal-linked-label {
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: var(--text-secondary);
}

.record-link-more-modal-linked-list {
    max-height: 120px;
    overflow-y: auto;
    margin-top: 4px;
}

.record-link-more-modal-linked-item {
    display: flex;
    justify-content: space-between;
    font-size: 12.5px;
    color: var(--text-primary);
    margin-top: 4px;
}

.record-link-more-modal-selection-summary {
    font-size: 12.5px;
    color: var(--color-brand);
    font-weight: 600;
    margin-top: 10px;
}

.record-link-more-modal-root {
    display: flex;
    flex-direction: column;
    max-height: 580px;
    overflow-y: auto;
}

.record-link-more-modal-list {
    flex: 1;
    min-height: 80px;
    overflow-y: auto;
    margin-top: 10px;
}

.record-link-more-modal-item {
    margin-bottom: 10px;
    cursor: pointer;
    transition: border-color 0.15s;
}

.record-link-more-modal-item-disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

.record-link-more-modal-item-warning {
    font-size: 11.5px;
    color: var(--color-danger);
    margin-top: 2px;
}

.record-link-more-modal-item-selected {
    border-color: var(--color-brand);
}

.record-link-more-modal-item-content {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
}

.record-link-more-modal-item-indicator {
    width: 20px;
    height: 20px;
    border-radius: 50%;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 11px;
    border: 2px solid var(--border-color);
    color: var(--text-inverse);
}

.record-link-more-modal-item-indicator-selected {
    background: var(--color-brand);
    border-color: var(--color-brand);
}

.record-link-more-modal-item-info {
    flex: 1;
    min-width: 0;
}

.record-link-more-modal-item-description {
    font-size: 14px;
    font-weight: 600;
    color: var(--text-primary);
}

.record-link-more-modal-item-side {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 6px;
    flex-shrink: 0;
}

.record-link-more-modal-item-value {
    font-weight: 700;
    font-size: 14px;
}

.record-link-more-modal-value-positive {
    color: var(--color-success);
}

.record-link-more-modal-value-negative {
    color: var(--color-danger);
}

.record-link-more-modal-empty {
    padding: 24px 16px;
    text-align: center;
    color: var(--text-muted);
    font-size: 13px;
}

.record-link-more-modal-actions {
    margin-top: 12px;
    flex-shrink: 0;
}
</style>
